import type { RootState } from "utils/redux/store";

// The activity tables cache one company selection at a time: data for a new selection replaces
// the cache instead of mixing into it, and responses for an older selection are dropped.
export interface ActivityCacheOwner {
  companyKey: string;
  requestId: string | null;
}

export const initialActivityCacheOwner: ActivityCacheOwner = {
  companyKey: "",
  requestId: null,
};

// Selecting only the current company returns the same data as selecting none, so both use the
// "" key and share the cache with the single-company views (e.g. a manager's own check-ins).
export const getActivityCompanyKey = (
  state: RootState,
  companyAccountId?: string
) => {
  if (!companyAccountId) return "";
  const currentCompanyId = state.global.companyInfo?.companyAccountId;
  return companyAccountId === String(currentCompanyId) ? "" : companyAccountId;
};

export const getActivityCompanyQuery = (companyKey: string) =>
  companyKey ? `?${new URLSearchParams({ companySelector: companyKey })}` : "";

// For a thunk's condition: false skips the request. A request for another company always goes
// ahead; for the cached company it is skipped while one is in flight or the data is cached.
export const shouldFetchActivityData = (
  owner: ActivityCacheOwner,
  companyKey: string,
  status: string,
  isCached: boolean
) => companyKey !== owner.companyKey || (status !== "loading" && !isCached);

// For a pending reducer. Returns true when the cache belongs to another company and must be emptied.
// The thunk's getPendingMeta adds companyKey to the meta; RTK 1.9 only types it when every
// generic of createAsyncThunk is spelled out, so it is read here instead.
export const startActivityRequest = (
  owner: ActivityCacheOwner,
  meta: { requestId: string }
) => {
  const { companyKey = "" } = meta as { companyKey?: string };
  owner.requestId = meta.requestId;
  if (owner.companyKey === companyKey) return false;
  owner.companyKey = companyKey;
  return true;
};
